import React, { useState, useEffect } from 'react';
import { Upload, Users, BarChart3, Download, FileText, Clock, CheckCircle, Trash2, AlertCircle } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import api from '../utils/api';
import QuizUpload from '../components/QuizUpload';
import QuizAssignment from '../components/QuizAssignment';
import QuizAttempts from '../components/QuizAttempts';

interface Quiz {
  id: string;
  title: string;
  subject: string;
  description?: string;
  timeLimit?: number;
  createdAt: string;
  creator?: { name: string };
  questions?: any[];
  assignments?: any[];
  attempts?: any[];
  assignedAt?: string;
  dueDate?: string;
  bestScore?: number;
  lastScore?: number;
  _count?: {
    questions: number;
    assignments: number;
    attempts: number;
  };
}

interface Student {
  id: string;
  name: string;
  email: string;
}

const QuizzesPage: React.FC = () => {
  const { user } = useAuth();
  const [quizzes, setQuizzes] = useState<Quiz[]>([]);
  const [students, setStudents] = useState<Student[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [activeTab, setActiveTab] = useState<'overview' | 'upload' | 'assign' | 'attempts'>('overview');
  const [selectedQuiz, setSelectedQuiz] = useState<Quiz | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);

  useEffect(() => {
    fetchQuizzes();
    if (user?.role === 'TUTOR') {
      fetchStudents();
    }
  }, [user]);

  const fetchQuizzes = async () => {
    try {
      setError('');
      const response = await api.get('/quizzes');
      setQuizzes(response.data);
    } catch (error: any) {
      setError(error.response?.data?.message || 'Failed to fetch quizzes');
    } finally {
      setLoading(false);
    }
  };

  const fetchStudents = async () => {
    try {
      const response = await api.get('/admin/students');
      setStudents(response.data);
    } catch (error: any) {
      console.error('Failed to fetch students:', error);
    }
  };

  const handleQuizUploaded = () => {
    fetchQuizzes();
    setSuccess('Quiz uploaded successfully!');
    setActiveTab('overview');
    setTimeout(() => setSuccess(''), 3000);
  };

  const handleQuizAssigned = () => {
    fetchQuizzes();
    setSuccess('Quiz assigned successfully!');
    setActiveTab('overview');
    setTimeout(() => setSuccess(''), 3000);
  };

  const handleDeleteQuiz = async (quizId: string) => {
    try {
      setError('');
      await api.delete(`/quizzes/${quizId}`);
      setQuizzes(quizzes.filter(quiz => quiz.id !== quizId));
      setSuccess('Quiz deleted successfully!');
      setDeleteConfirm(null);
      setTimeout(() => setSuccess(''), 3000);
    } catch (error: any) {
      setError(error.response?.data?.message || 'Failed to delete quiz');
      setDeleteConfirm(null);
    }
  };

  const getSubjectColor = (subject: string) => {
    const colors = {
      VOCABULARY: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
      CLOZE: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
      COMPREHENSION: 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200',
      MATHS: 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200'
    };
    return colors[subject as keyof typeof colors] || 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200';
  };

  const formatScore = (score: number | null | undefined) => {
    if (score === null || score === undefined) return 'N/A';
    return `${Math.round(score)}%`;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600 dark:text-gray-300">Loading quizzes...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100">
            {user?.role === 'TUTOR' ? 'Quiz Management' : 'My Quizzes'}
          </h1>
          <p className="mt-2 text-gray-600 dark:text-gray-300">
            {user?.role === 'TUTOR'
              ? 'Create, assign, and track student quiz performance'
              : 'Complete your assigned quizzes and track your progress'
            }
          </p>
        </div>

        {/* Success/Error Messages */}
        {success && (
          <div className="mb-6 bg-green-50 dark:bg-green-900 border border-green-200 dark:border-green-700 rounded-lg p-4">
            <div className="flex items-center">
              <CheckCircle className="w-5 h-5 text-green-600 mr-2" />
              <p className="text-green-800 dark:text-green-200">{success}</p>
            </div>
          </div>
        )}

        {error && (
          <div className="mb-6 bg-red-50 dark:bg-red-900 border border-red-200 dark:border-red-700 rounded-lg p-4">
            <p className="text-red-800 dark:text-red-200">{error}</p>
          </div>
        )}

        {user?.role === 'TUTOR' ? (
          <>
            {/* Tutor Navigation */}
            <div className="mb-8">
              <nav className="flex space-x-1 bg-white dark:bg-gray-800 rounded-lg p-1 shadow-sm">
                {[
                  { key: 'overview', label: 'Overview', icon: BarChart3 },
                  { key: 'upload', label: 'Upload Quiz', icon: Upload },
                  { key: 'assign', label: 'Assign Quiz', icon: Users },
                  { key: 'attempts', label: 'View Attempts', icon: FileText }
                ].map(({ key, label, icon: Icon }) => (
                  <button
                    key={key}
                    onClick={() => setActiveTab(key as any)}
                    className={`flex items-center px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                      activeTab === key
                        ? 'bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300'
                        : 'text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-700'
                    }`}
                  >
                    <Icon className="w-4 h-4 mr-2" />
                    {label}
                  </button>
                ))}
              </nav>
            </div>

            {/* Tutor Content */}
            {activeTab === 'overview' && (
              <div className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                  <div className="bg-white dark:bg-gray-800 rounded-lg p-6 shadow-sm">
                    <div className="flex items-center">
                      <FileText className="w-8 h-8 text-blue-600" />
                      <div className="ml-4">
                        <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">{quizzes.length}</p>
                        <p className="text-sm text-gray-600 dark:text-gray-300">Total Quizzes</p>
                      </div>
                    </div>
                  </div>

                  <div className="bg-white dark:bg-gray-800 rounded-lg p-6 shadow-sm">
                    <div className="flex items-center">
                      <Users className="w-8 h-8 text-green-600" />
                      <div className="ml-4">
                        <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                          {quizzes.reduce((sum, quiz) => sum + (quiz._count?.assignments || 0), 0)}
                        </p>
                        <p className="text-sm text-gray-600 dark:text-gray-300">Total Assignments</p>
                      </div>
                    </div>
                  </div>

                  <div className="bg-white dark:bg-gray-800 rounded-lg p-6 shadow-sm">
                    <div className="flex items-center">
                      <BarChart3 className="w-8 h-8 text-purple-600" />
                      <div className="ml-4">
                        <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                          {quizzes.reduce((sum, quiz) => sum + (quiz._count?.attempts || 0), 0)}
                        </p>
                        <p className="text-sm text-gray-600 dark:text-gray-300">Total Attempts</p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Quiz List */}
                <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm">
                  <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
                    <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Your Quizzes</h2>
                  </div>

                  {quizzes.length === 0 ? (
                    <div className="p-8 text-center">
                      <FileText className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                      <p className="text-gray-600 dark:text-gray-300 mb-4">No quizzes created yet</p>
                      <button
                        onClick={() => setActiveTab('upload')}
                        className="btn btn-primary"
                      >
                        Create Your First Quiz
                      </button>
                    </div>
                  ) : (
                    <div className="divide-y divide-gray-200 dark:divide-gray-700">
                      {quizzes.map((quiz) => (
                        <div key={quiz.id} className="p-6 hover:bg-gray-50 dark:hover:bg-gray-700">
                          <div className="flex items-center justify-between">
                            <div className="flex-1">
                              <div className="flex items-center space-x-3 mb-2">
                                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">{quiz.title}</h3>
                                <span className={`px-2 py-1 rounded-full text-xs font-medium ${getSubjectColor(quiz.subject)}`}>
                                  {quiz.subject}
                                </span>
                                {quiz.timeLimit && (
                                  <div className="flex items-center text-sm text-gray-500 dark:text-gray-400">
                                    <Clock className="w-4 h-4 mr-1" />
                                    {Math.floor(quiz.timeLimit / 60)}:{(quiz.timeLimit % 60).toString().padStart(2, '0')}
                                  </div>
                                )}
                              </div>

                              {quiz.description && (
                                <p className="text-gray-600 dark:text-gray-300 mb-2">{quiz.description}</p>
                              )}

                              <div className="flex items-center space-x-4 text-sm text-gray-500 dark:text-gray-400">
                                <span>{quiz._count?.questions || 0} questions</span>
                                <span>{quiz._count?.assignments || 0} assignments</span>
                                <span>{quiz._count?.attempts || 0} attempts</span>
                              </div>
                            </div>

                            <div className="flex space-x-2">
                              <button
                                onClick={() => {
                                  setSelectedQuiz(quiz);
                                  setActiveTab('assign');
                                }}
                                className="btn btn-secondary text-sm"
                              >
                                Assign
                              </button>
                              <button
                                onClick={() => {
                                  setSelectedQuiz(quiz);
                                  setActiveTab('attempts');
                                }}
                                className="btn btn-outline text-sm"
                              >
                                View Attempts
                              </button>
                              <button
                                onClick={() => setDeleteConfirm(quiz.id)}
                                className="btn text-sm bg-red-50 dark:bg-red-900 text-red-600 dark:text-red-200 hover:bg-red-100 dark:hover:bg-red-800 border border-red-200 dark:border-red-700"
                              >
                                <Trash2 className="w-4 h-4 mr-1" />
                                Delete
                              </button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}

            {activeTab === 'upload' && (
              <QuizUpload onUploadSuccess={handleQuizUploaded} />
            )}

            {activeTab === 'assign' && (
              <QuizAssignment
                quizzes={quizzes}
                students={students}
                selectedQuiz={selectedQuiz}
                onAssignSuccess={handleQuizAssigned}
              />
            )}

            {activeTab === 'attempts' && (
              <QuizAttempts
                quizzes={quizzes}
                selectedQuiz={selectedQuiz}
              />
            )}
          </>
        ) : (
          /* Student View */
          <div className="space-y-6">
            {quizzes.length === 0 ? (
              <div className="bg-white dark:bg-gray-800 rounded-lg p-8 text-center">
                <FileText className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-600 dark:text-gray-300">No quizzes assigned to you yet</p>
              </div>
            ) : (
              <div className="grid gap-6">
                {quizzes.map((quiz) => (
                  <div key={quiz.id} className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6">
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center space-x-3">
                        <h3 className="text-xl font-semibold text-gray-900 dark:text-gray-100">{quiz.title}</h3>
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${getSubjectColor(quiz.subject)}`}>
                          {quiz.subject}
                        </span>
                        {quiz.timeLimit && (
                          <div className="flex items-center text-sm text-gray-500 dark:text-gray-400">
                            <Clock className="w-4 h-4 mr-1" />
                            {Math.floor(quiz.timeLimit / 60)}:{(quiz.timeLimit % 60).toString().padStart(2, '0')}
                          </div>
                        )}
                      </div>

                      <button
                        onClick={() => window.location.href = `/quiz/${quiz.id}`}
                        className="btn btn-primary"
                      >
                        Start Quiz
                      </button>
                    </div>

                    {quiz.description && (
                      <p className="text-gray-600 dark:text-gray-300 mb-4">{quiz.description}</p>
                    )}

                    <div className="grid grid-cols-2 md:grid-cols-5 gap-4 text-sm">
                      <div>
                        <span className="text-gray-500 dark:text-gray-400">Questions:</span>
                        <span className="ml-2 font-medium">{quiz._count?.questions || 0}</span>
                      </div>
                      <div>
                        <span className="text-gray-500 dark:text-gray-400">Attempts:</span>
                        <span className="ml-2 font-medium">{quiz.attempts || 0}</span>
                      </div>
                      <div>
                        <span className="text-gray-500 dark:text-gray-400">Best Score:</span>
                        <span className="ml-2 font-medium text-green-600">{formatScore(quiz.bestScore)}</span>
                      </div>
                      <div>
                        <span className="text-gray-500 dark:text-gray-400">Last Score:</span>
                        <span className="ml-2 font-medium">{formatScore(quiz.lastScore)}</span>
                      </div>
                      <div>
                        <span className="text-gray-500 dark:text-gray-400">Due Date:</span>
                        <span className={`ml-2 font-medium ${quiz.dueDate ? (new Date(quiz.dueDate) < new Date() ? 'text-red-600' : 'text-orange-600') : 'text-gray-400 dark:text-gray-500'}`}>
                          {quiz.dueDate ? new Date(quiz.dueDate).toLocaleDateString() : 'No due date'}
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Delete Confirmation Modal */}
        {deleteConfirm && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-md w-full mx-4">
              <div className="flex items-center mb-4">
                <AlertCircle className="w-6 h-6 text-red-600 mr-2" />
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Confirm Deletion</h3>
              </div>

              <p className="text-gray-600 dark:text-gray-300 mb-6">
                Are you sure you want to delete this quiz? This action cannot be undone.
                All associated questions, assignments, and student attempts will be permanently deleted.
              </p>

              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => setDeleteConfirm(null)}
                  className="btn btn-outline text-sm"
                >
                  Cancel
                </button>
                <button
                  onClick={() => handleDeleteQuiz(deleteConfirm)}
                  className="btn text-sm bg-red-600 text-white hover:bg-red-700"
                >
                  <Trash2 className="w-4 h-4 mr-1" />
                  Delete Quiz
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default QuizzesPage;